import type { Language } from '@eformulieren/engine'
import { InstantieId, InvitationCode } from '@eformulieren/model'
import type { InvitationDetails, UnusableReason } from '@eformulieren/renderer'
import { z } from 'zod'
import { adviserPath } from './adviserRoute.js'
import { codeFromBody } from './rejection.js'

/**
 * De pagina achter de link uit een uitnodiging (R-64c, R-155e, ontwerp 4.8 en 9.2,
 * koppelvlak.md hoofdstuk 3).
 *
 * De opslagdienst zet de link `<adres van de publicatie>/uitnodiging#<code>` in de outbox, en het
 * processysteem brengt hem bij de ontvanger (R-64b). Het deel achter het hekje stuurt een browser
 * niet naar een server, dus de code komt in geen toegangslog. Deze pagina leest haar, haalt haar
 * meteen uit de adresbalk (`history.replaceState` in `main.tsx`), en biedt haar pas aan als de
 * ontvanger kiest: aannemen of weigeren.
 *
 * **Waar de code blijft tot na het inloggen.** In `sessionStorage` van dit tabblad, onder een eigen
 * sleutel, en nergens anders: niet in een query (die staat in elk log), niet in een cookie (dat
 * reist naar elke zusterhost onder het cookiedomein, zie `cookie.ts` in de pod) en niet in de
 * console. Ze blijft staan zolang de uitnodiging nog te beantwoorden is, ook als iemand de pagina
 * ververst nadat het fragment al weg is, en gaat weg zodra er een antwoord is: aangenomen,
 * geweigerd of ongeldig. Bij een storing, te veel pogingen of een verlopen sessie blijft ze, want
 * dan is de uitnodiging nog niet beantwoord.
 *
 * **De weg naar het inloggen** (opdracht 5c). Zonder sessie biedt de pagina per ingang van de pod
 * een knop naar de inlogdienst (`inloggen.ts`), met het adres van deze pagina zonder fragment als
 * doel. Het fragment is de code; die gaat niet naar een andere dienst, want ze staat al in dit
 * tabblad. Na het inloggen stuurt de inlogdienst terug naar `/uitnodiging`, haalt de pagina de code
 * uit het tabblad en biedt meteen aannemen of weigeren. Een fragment overleeft de omweg langs de
 * identiteitsprovider niet betrouwbaar; de bewaarde code in het tabblad wel.
 */

/** De sleutel in `sessionStorage`; een eigen naam naast `proef` (`Formulierpagina.tsx`). */
export const STORAGE_KEY = 'eformulieren-uitnodiging'

/** Het deel van `Storage` dat deze pagina gebruikt; injecteerbaar voor de test. */
export type CodeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** Of dit pad de pagina van de uitnodiging is (`/uitnodiging`, met of zonder sluitende slash). */
export function isInvitationPath(path: string): boolean {
  return path.replace(/\/+$/, '') === '/uitnodiging'
}

/**
 * De code uit het fragment, of niets.
 *
 * Getoetst op `InvitationCode` uit het model, de enige plek waar de vorm staat: iets anders dan
 * een code gaat niet naar de pod en niet in de opslag.
 */
export function codeFromFragment(fragment: string): string | undefined {
  const candidate = fragment.startsWith('#') ? fragment.slice(1) : fragment
  return InvitationCode.safeParse(candidate).success ? candidate : undefined
}

/**
 * Een opslag die gooit (geblokkeerde sitegegevens, een privévenster) is geen reden om de pagina te
 * laten vallen: dan is er geen bewaarde code, en dat is dezelfde uitkomst als een leeg tabblad.
 */
function safe<T>(work: () => T, overrides: T): T {
  try {
    return work()
  } catch {
    return overrides
  }
}

export function saveCode(storage: CodeStorage, code: string): void {
  safe(() => {
    storage.setItem(STORAGE_KEY, code)
  }, undefined)
}

export function readSavedCode(storage: CodeStorage): string | undefined {
  const saved = safe(() => storage.getItem(STORAGE_KEY), null)
  return saved === null ? undefined : codeFromFragment(saved)
}

export function clearCode(storage: CodeStorage): void {
  safe(() => {
    storage.removeItem(STORAGE_KEY)
  }, undefined)
}

/** Wat een antwoord van de pod voor de ontvanger betekent. */
export type Outcome =
  /** Aangenomen: door naar het formulier van deze instantie. */
  | { readonly soort: 'naar-formulier'; readonly instantieId: string }
  /** Geweigerd door de ontvanger zelf. */
  | { readonly soort: 'geweigerd' }
  /** De code is niet (meer) geldig. Eén uitkomst voor elke reden (R-64c). */
  | { readonly soort: 'ongeldig' }
  /** Geen sessie (meer): eerst inloggen. De code blijft. */
  | { readonly soort: 'inloggen' }
  /** Nog niet beantwoord: te veel pogingen, een storing, of geen verbinding. De code blijft. */
  | { readonly soort: 'opnieuw'; readonly code?: string }

/**
 * Wat een antwoord van `POST /uitnodiging` of `POST /uitnodiging/weigeren` betekent.
 *
 * **Elke weigering van de code is één uitkomst** (R-64c): de pod geeft voor een onbekende,
 * verlopen, gebruikte of andermans code dezelfde 404 `uitnodiging-ongeldig`, en een code die niet
 * de vorm heeft is een 400. Voor de ontvanger is dat allemaal hetzelfde, en de pagina zegt er geen
 * reden bij.
 *
 * **De instantie uit het antwoord wordt getoetst.** Ze komt in het pad van de volgende pagina; een
 * waarde die geen instantie-id is, gaat daar niet in.
 */
export function outcomeFromResponse(operation: 'aannemen' | 'weigeren', status: number, body: string): Outcome {
  if (status === 200) {
    if (operation === 'weigeren') return { soort: 'geweigerd' }
    const instantieId = instantieFromBody(body)
    return instantieId === undefined ? { soort: 'opnieuw' } : { soort: 'naar-formulier', instantieId }
  }
  if (status === 404 || status === 400) return { soort: 'ongeldig' }
  if (status === 401) return { soort: 'inloggen' }
  const code = codeFromBody(body)
  return code === undefined ? { soort: 'opnieuw' } : { soort: 'opnieuw', code }
}

function instantieFromBody(body: string): string | undefined {
  try {
    const read = JSON.parse(body) as { instantie?: unknown }
    const instantie = InstantieId.safeParse(read.instantie)
    return instantie.success ? instantie.data : undefined
  } catch {
    return undefined
  }
}

/**
 * De code bij binnenkomst op `/uitnodiging` (R-64c).
 *
 * Een code in het fragment wint en wordt bewaard. Zonder fragment (na het inloggen of na verversen
 * is het al gewist) de bewaarde code uit dit tabblad. **Een fragment dat geen code is, geeft geen code
 * en wist de bewaarde**: wie een andere link opent, bedoelt die link. In de voor-opnamen toonde een
 * link met een foute code in hetzelfde tabblad nog de vorige, geldige uitnodiging, omdat de bewaarde
 * code won van het foute fragment. Een leeg hekje is geen fragment.
 */
export function codeOnArrival(fragment: string, storage: CodeStorage): string | undefined {
  const given = fragment.startsWith('#') ? fragment.slice(1) : fragment
  if (given === '') return readSavedCode(storage)
  const fromFragment = codeFromFragment(given)
  if (fromFragment === undefined) {
    clearCode(storage)
    return undefined
  }
  saveCode(storage, fromFragment)
  return fromFragment
}

const Bilingual = z.object({ nl: z.string(), en: z.string() })

/**
 * Het antwoord van `POST /uitnodiging/inzien` (`api-contract.md` 2). Niet strikt: de pagina leest wat
 * ze toont, en een veld erbij van de pod breekt haar niet.
 */
const InvitationView = z.object({
  instantie: InstantieId,
  formulier: z.object({ titel: Bilingual }),
  uitgenodigdDoor: z.enum(['aanvrager', 'gemachtigde', 'medewerker-nwo']),
  doel: z.enum(['deling', 'advies']),
  rechten: z.array(z.object({ sectieId: z.string(), titel: Bilingual, recht: z.enum(['lezen', 'bewerken']) })).optional(),
  geldigTot: z.string().min(1),
  ingelogdAls: z.string().min(1),
  kant: z.enum(['intern', 'extern']).optional(),
})

/** Wat het bekijken van een uitnodiging voor de pagina betekent. */
export type ViewOutcome =
  | { readonly soort: 'bekijken'; readonly doel: 'deling' | 'advies'; readonly uitnodiging: InvitationDetails }
  /** Onbekend, gebruikt, ingetrokken of een ander adres: één uitkomst (R-64c). */
  | { readonly soort: 'ongeldig' }
  /**
   * Al aangenomen, door wie nu kijkt: naar het formulier of de adviseurpagina, in plaats van de
   * melding dat de uitnodiging niet meer te gebruiken is (R-64c, 31 oktober 2026).
   */
  | { readonly soort: 'al-aangenomen'; readonly instantieId: string; readonly doel: 'deling' | 'advies' }
  /** Het adres klopte, en de uitnodiging is niet meer te gebruiken (besluit 4). */
  | { readonly soort: 'onbruikbaar'; readonly reden: UnusableReason; readonly geldigTot?: string }
  /** Een adviesvraag voor de andere kant dan de ingang waarlangs is ingelogd: binnen of buiten NWO. */
  | { readonly soort: 'andere-ingang'; readonly kant: 'intern' | 'extern' }
  | { readonly soort: 'inloggen' }
  /** De pod toonde haar niet (geen route, een storing): de keuze zonder gegevens, zoals in fase 1. */
  | { readonly soort: 'zonder-gegevens' }

const UNUSABLE: Readonly<Record<string, UnusableReason>> = {
  'uitnodiging-verlopen': 'verlopen',
  'uitnodiging-geweigerd': 'geweigerd',
  'uitnodiging-ingetrokken': 'ingetrokken',
  'uitnodiging-al-aangenomen': 'al-aangenomen',
  'uitnodiging-vervallen': 'vervallen',
}

/**
 * Wat een antwoord van `POST /uitnodiging/inzien` betekent (besluit 4 en 5, audit G-06 en G-08).
 *
 * **Op de code in het lichaam, niet op de status.** `uitnodiging-ongeldig` is de ene melding zonder
 * reden; de vijf codes met een reden komen alleen als het adres in het token dat van de uitnodiging is.
 * Een kale 404 zonder die code is een pod zonder deze route, en dan biedt de pagina de keuze van fase
 * 1: anders heette elke geldige uitnodiging onbruikbaar. Een 400 is een code die niet de vorm heeft,
 * en die is ongeldig.
 */
export function viewOutcome(status: number, body: string, language: Language): ViewOutcome {
  if (status === 401) return { soort: 'inloggen' }
  if (status === 400) return { soort: 'ongeldig' }
  const code = codeFromBody(body)
  if (status === 200) {
    try {
      const read = InvitationView.safeParse(JSON.parse(body))
      if (!read.success) return { soort: 'zonder-gegevens' }
      const view = read.data
      const rights = view.doel === 'advies' ? [] : (view.rechten ?? [])
      return {
        soort: 'bekijken',
        doel: view.doel,
        uitnodiging: {
          formulier: view.formulier.titel[language],
          uitgenodigdDoor: view.uitgenodigdDoor,
          doel: view.doel,
          schrijven: rights.filter((right) => right.recht === 'bewerken').map((right) => right.titel[language]),
          lezen: rights.filter((right) => right.recht === 'lezen').map((right) => right.titel[language]),
          geldigTot: view.geldigTot,
          ingelogdAls: view.ingelogdAls,
        },
      }
    } catch {
      return { soort: 'zonder-gegevens' }
    }
  }
  if (code === 'uitnodiging-ongeldig') return { soort: 'ongeldig' }
  if (code === 'ingang-van-nwo-nodig') return { soort: 'andere-ingang', kant: 'intern' }
  if (code === 'ingang-buiten-nwo-nodig') return { soort: 'andere-ingang', kant: 'extern' }
  if (code === 'uitnodiging-al-aangenomen') {
    // De pod noemt de instantie alleen aan wie haar zelf aannam; zonder instantie blijft het de melding.
    const own = ownAcceptance(body)
    if (own !== undefined) return { soort: 'al-aangenomen', ...own }
  }
  const reason = code === undefined ? undefined : UNUSABLE[code]
  if (reason !== undefined) {
    const until = validUntilFromBody(body)
    return until === undefined ? { soort: 'onbruikbaar', reden: reason } : { soort: 'onbruikbaar', reden: reason, geldigTot: until }
  }
  return { soort: 'zonder-gegevens' }
}

function ownAcceptance(body: string): { readonly instantieId: string; readonly doel: 'deling' | 'advies' } | undefined {
  try {
    const read = z.object({ instantie: InstantieId, doel: z.enum(['deling', 'advies']) }).safeParse(JSON.parse(body))
    return read.success ? { instantieId: read.data.instantie, doel: read.data.doel } : undefined
  } catch {
    return undefined
  }
}

function validUntilFromBody(body: string): string | undefined {
  try {
    const read = z.object({ geldigTot: z.string().min(1) }).safeParse(JSON.parse(body))
    return read.success ? read.data.geldigTot : undefined
  } catch {
    return undefined
  }
}

/** Waar aannemen heen gaat: het formulier, of bij advies de adviseurpagina (`adviserRoute.ts`). */
export function targetAfterAccept(instantieId: string, doel: 'deling' | 'advies'): string {
  return doel === 'advies' ? adviserPath(instantieId) : `/${encodeURIComponent(instantieId)}`
}
